import React, { useContext, useEffect } from "react"
import { OreveButton } from "components/button/OreveButton.tsx"
import { MainPageContentContainer } from "components/MainPageContentContainer"
import { LinearProgress, Typography } from "@mui/material"
import { Box } from "@mui/system"
import ActivatedCard from "assets/svg/Type=Activation confirmee.svg"
import CreditCardIcon from "@mui/icons-material/CreditCard"
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined"
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined"
import { Loader } from "components/Loader.tsx"
import { RfidActivationFormContext } from "./rfidActivationForm.tsx"
import { useMmsService } from "services/mms/context/MmsServiceContext.tsx"
import { RfidStatus } from "layouts/rfid/utils/const.ts"

interface ValidationStepProps {
    goBackToList: () => void
    rfidActivationSuccess: boolean | null
}

export const ValidationStep: React.FC<ValidationStepProps> = ({
    goBackToList,
    rfidActivationSuccess,
}) => {
    const context = useContext(RfidActivationFormContext)
    const { rfidService } = useMmsService()
    const { useUpdateRfidCard } = rfidService
    const { sendRequest: updateCard, success: updateCardStatus } =
        useUpdateRfidCard(context?.chosenCard?.id ?? "")

    useEffect(() => {
        if (rfidActivationSuccess === null) {
            updateCard({
                ...context?.chosenCard,
                cardHolderId: context?.chosenAccount?.info.id,
                status: RfidStatus.Active,
            })
        }
    }, [rfidActivationSuccess])

    return (
        <MainPageContentContainer
            sx={{
                position: "absolute",
                padding: 0,
                margin: 0,
                width: "calc(100vw - (1.5rem + 1rem))",
                height: "calc(100vh - (1.5rem + 132px + 2.5rem ))",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: "100%",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "1.5rem",
                        mt: { xs: "4rem", md: "6rem" },
                        width: { xs: "100%", md: "50%" },
                        px: { xs: 2, sm: 4 },
                    }}
                >
                    {rfidActivationSuccess === null ? (
                        <Loader />
                    ) : rfidActivationSuccess && updateCardStatus ? (
                        <ActivationSuccess goBackToList={goBackToList} />
                    ) : (
                        <ActivationFailed goBackToList={goBackToList} />
                    )}
                </Box>

                <Box
                    sx={{
                        display: { xs: "none", md: "flex" },
                        bgcolor: "lightgrey.main",
                        borderRadius: { xs: 0, md: "0 20px 20px 0" },
                        width: { xs: "100%", md: "50%" },
                        height: "calc(100vh - (1.5rem + 132px + 2.5rem ))",
                        justifyContent: "center",
                        alignItems: "center",
                        overflow: "hidden",
                        "& img": {
                            width: "100%",
                            height: "auto",
                            maxWidth: "100%",
                            objectFit: "contain",
                            padding: "2rem",
                        },
                    }}
                >
                    <img
                        src={ActivatedCard}
                        alt="Activation confirmée"
                        style={{ maxWidth: "80%", height: "auto" }}
                    />
                </Box>
            </Box>
        </MainPageContentContainer>
    )
}

interface FeedbackProps {
    goBackToList: () => void
}

const ActivationSuccess: React.FC<FeedbackProps> = ({ goBackToList }) => {
    const context = useContext(RfidActivationFormContext)

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <CheckCircleOutlineOutlinedIcon
                    sx={{
                        color: "green",
                        mb: "0.5rem",
                        width: "3rem",
                        height: "3rem",
                    }}
                />
            </Box>
            <Typography variant="h4" textAlign="center">
                Activation confirmée
            </Typography>
            <Typography variant="body2" sx={{ mb: "10px" }}>
                4/4
            </Typography>
            <LinearProgress
                sx={{
                    width: "100px",
                    borderRadius: "20px",
                    bgcolor: "grey.100",
                }}
                color="secondary"
                variant="determinate"
                value={100}
            />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "1rem",
                    my: { xs: "2rem", md: "4rem" },
                    maxWidth: { xs: "90%", md: "40%" },
                    textAlign: "center",
                }}
            >
                <Typography variant="body1" color="grey.500">
                    La carte n° {context?.chosenCard?.rfidNumber} associée{" "}
                    {context?.chosenAccount?.type === "vehicle"
                        ? "au véhicule " +
                          context?.chosenAccount?.info.licensePlate
                        : "au conducteur " +
                          context?.chosenAccount?.info.name}{" "}
                    a été activée avec succès !
                </Typography>
                <OreveButton
                    data-cy="rfid__activation--return"
                    icon={CreditCardIcon}
                    value="Retour à la liste des cartes"
                    onClick={goBackToList}
                />
            </Box>
        </>
    )
}

const ActivationFailed: React.FC<FeedbackProps> = ({ goBackToList }) => {
    const context = useContext(RfidActivationFormContext)

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <ErrorOutlineOutlinedIcon
                    sx={{
                        color: "red",
                        mb: "0.5rem",
                        width: "3rem",
                        height: "3rem",
                    }}
                />
            </Box>
            <Typography variant="h4" textAlign="center">
                Échec de l’activation
            </Typography>
            <Typography variant="body2" sx={{ mb: "10px" }}>
                4/4
            </Typography>
            <LinearProgress
                sx={{
                    width: "100px",
                    borderRadius: "20px",
                    bgcolor: "grey.100",
                }}
                color="error"
                variant="determinate"
                value={100}
            />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "1rem",
                    my: { xs: "2rem", md: "4rem" },
                    maxWidth: { xs: "90%", md: "40%" },
                    textAlign: "center",
                }}
            >
                <Typography variant="body1" color="grey.500">
                    Une erreur est survenue sur la carte n°{" "}
                    {context?.chosenCard?.rfidNumber}. Contactez le support pour
                    résoudre le problème.
                </Typography>
                <OreveButton
                    icon={CreditCardIcon}
                    value="Retour à la liste des cartes"
                    onClick={goBackToList}
                />
            </Box>
        </>
    )
}
